import CryptoJS from 'crypto-js';

const SECRET_KEY = process.env.NEXT_PUBLIC_PHONE_SECRET;

type LaravelEncryptedPayload = {
  iv: string;
  value: string;
  mac: string;
  tag?: string;
};

function getLaravelKey() {
  if (!SECRET_KEY) return null;

  // Matches PHP: hash('sha256', config('common.string_encryption_key'), true)
  return CryptoJS.SHA256(SECRET_KEY);
}

function safeUtf8ToString(wordArray: CryptoJS.lib.WordArray): string | null {
  try {
    const result = wordArray.toString(CryptoJS.enc.Utf8);
    return result || null;
  } catch {
    return null;
  }
}

function isValidMac(
  payload: LaravelEncryptedPayload,
  key: CryptoJS.lib.WordArray,
) {
  const calculatedMac = CryptoJS.HmacSHA256(
    payload.iv + payload.value,
    key,
  ).toString();

  return calculatedMac === payload.mac;
}

/**
 * Decrypts Laravel Encrypter strings (AES-256-CBC + encryptString format).
 */
export function decryptPayload(
  encrypted: string | undefined | null,
): string | null {
  if (!encrypted || !SECRET_KEY) return null;

  try {
    const key = getLaravelKey();
    if (!key) return null;

    const payloadJson = CryptoJS.enc.Utf8.stringify(
      CryptoJS.enc.Base64.parse(encrypted.trim()),
    );
    const payload = JSON.parse(payloadJson) as LaravelEncryptedPayload;

    if (!payload?.iv || !payload?.value || !payload?.mac) {
      return null;
    }

    if (!isValidMac(payload, key)) {
      console.error('Decryption failed: invalid MAC');
      return null;
    }

    const iv = CryptoJS.enc.Base64.parse(payload.iv);
    const ciphertext = CryptoJS.enc.Base64.parse(payload.value);
    const cipherParams = CryptoJS.lib.CipherParams.create({ ciphertext });

    const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return safeUtf8ToString(decrypted);
  } catch (error) {
    console.error('Decryption failed:', error);
    return null;
  }
}
